/**
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React, {
  MouseEvent,
  useCallback,
  useState
} from 'react';
import {
  ClickAwayListener,
  makeStyles,
  Paper,
  Popper
} from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import {translate} from '@ion/core/src/locale/locale';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import LoadingText from '@ion/dls/src/components/loading/LoadingText';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import DeletePrompt from '../../editors/DeletePrompt';
import {DLSTheme} from '../../types/types';
import SavedColumns from './SavedColumns';
import {
  ColumnFormatType,
  HIDE_DURATION,
  SavedColumnItem
} from './types';

export interface SavedColumnListProps {
  removeSavedColumnViews?: (savedColumnName: string) => void;
  updateSavedColumns?: (savedColumnName: string, isNotAllowToSave?: boolean) => void;
  savedColumnViews?: SavedColumnItem[];
  isOpen?: boolean;
  isListLoading?: boolean;
  onClose?: () => void;
  anchorEl?: HTMLButtonElement;
  checkIsDeleteDialogOpen?: (isOpen?: boolean) => void;
  onSelectColumnList?: (selectedColumns: string) => void;
  onReset?: Function;
  selectedColumnName?: string;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    popper : {
      zIndex : dlsZIndex('savedColumnEditorPoper')
    },
    paper : {
      maxHeight : '340px',
      overflowY : 'auto',
      maxWidth  : '290px'
    },
    poperTitle : {
      display        : 'flex',
      justifyContent : 'space-between',
      padding        : '16px 10px',
      alignItems     : 'right',
      fontSize       : 16,
      fontFamily     : theme.typography.fontFamily
    },
    label : {
      whiteSpace   : 'nowrap',
      overflow     : 'hidden',
      textOverflow : 'ellipsis',
      fontSize     : 12,
      width        : '165px'
    },
    columnList : {
      margin           : '0 !important',
      paddingBottom    : '0 !important',
      '& :first-child' : {
        marginRight : '0 !important'
      },
      '& :last-child' : {
        marginLeft : '0 !important'
      }
    },
    deleteIconStyle : {
      position : 'relative',
      top      : '3px'
    },
    loadingTextWrapper : {
      gap            : 4,
      padding        : '3px 15px !important',
      width          : 'auto',
      height         : 45,
      display        : 'flex',
      justifyContent : 'center',
      cursor         : 'auto'
    },
    loadingText : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center',
      width          : 220
    }
  };
});

/**
 * @SavedColumnList
 *
 * The SavedColumnList provides a UI to show saved column in a dropdown list,
 * which allows the customer to select a saved column view.
 *
 * ```typescript
 *
 *    <SavedColumnList isOpen={isOpenSavedColumnList}
 *      anchorEl={columnListAnchorEliement}
 *      isListLoading={isListLoading}
 *      savedColumnViews={savedColumnViews}
 *      selectedColumnName={selectedColumnName}
 *      updateSavedColumns={updateSavedColumns}
 *      onSelectColumnList={onSelectColumnList}
 *      checkIsDeleteDialogOpen={checkIsDeleteDialogOpen}
 *      onClose={onCloseSavedColumnList} />
 * ```
 *
 * @see SavedColumnContainer
 */
export const SavedColumnList = ({
  savedColumnViews,
  anchorEl,
  onClose,
  selectedColumnName,
  onSelectColumnList,
  updateSavedColumns,
  removeSavedColumnViews,
  checkIsDeleteDialogOpen,
  onReset,
  isListLoading = false,
  isOpen = false
}: SavedColumnListProps) => {
  const classes                                 = useStyles();
  const [deletedViewName, setDeletedViewName]   = useState<string>('');
  const [selectedViewName, setSelectedViewName] = useState<string>(selectedColumnName);
  const [columnsForDelete, setColumnsForDelete] = useState<ColumnFormatType[]>();
  const [deleteDialog, setDeleteDialog]         = useState(false);
  const [isShowSnackbar, setIsShowSnackbar]     = useState(false);
  const [isDeleting, setIsDeleting]             = useState(false);

  const {columnPreference, deleteSuccess} = translate('common:columnChooser.savedColumnList', {
    returnObjects : true,
    columnName    : deletedViewName
  });

  const getDeleteMessage = useCallback(() => {
    return translate('common:editors.delete.singleDeleteMessage', {entity: deletedViewName});
  }, [deletedViewName]);

  /**
   * Invoked when the user click on Saved Columns dropdown list item
   * to select a saved column view.
   */
  const onItemSelect = useCallback(function(
    {viewName, columns}: SavedColumnItem) {
    setSelectedViewName(viewName);
    onSelectColumnList(columns);
    updateSavedColumns(viewName);
  }, []);

  /**
   * Invoked when the user click on delete icon of a particular list item
   * inside saved column dropdown list.
   */
  const onDelete = useCallback(function(e: MouseEvent<HTMLButtonElement>,
    {viewName, columns}: SavedColumnItem) {
    e.stopPropagation();
    setDeleteDialog(true);
    checkIsDeleteDialogOpen(true);
    setDeletedViewName(viewName);
    setColumnsForDelete(safeJSONParse(columns));
  }, []);

  /**
   * Invoked when the user click on Confirm Delete button inside DeletePrompt to
   * remove the Saved column view from the list
   */
  const onDeleteConfirm = useCallback(() => {
    setIsDeleting(true);
    removeSavedColumnViews(deletedViewName);
    setIsDeleting(false);
    setDeleteDialog(false);
    setIsShowSnackbar(true);
    if (deletedViewName === selectedViewName) {
      onReset();
    }
  }, [deletedViewName, selectedViewName]);

  /**
   * Invoked when the user click on cancel button and chooses to close
   * the DeletePrompt dialog.
   */
  const onDeleteCancel = useCallback(() => {
    checkIsDeleteDialogOpen(false);
    setDeleteDialog(false);
  }, []);

  /**
   * Invoked when the user chooses to close the snackbar message.
   */
  const onCloseMessage = useCallback(function() {
    setIsShowSnackbar(false);
  }, []);

  return (
    <ClickAwayListener onClickAway={onClose}>
      <div>
        <Popper open={isOpen}
          anchorEl={anchorEl}
          role={'dialog'}
          transition
          disablePortal
          placement='bottom'
          className={classes.popper}>
          <Paper classes={{root: classes.paper}}>
            <MenuList classes={{root: classes.columnList}}>
              {isListLoading ? (
                <MenuItem className={classes.loadingTextWrapper}>
                  <span className={classes.loadingText}><LoadingText /></span>
                </MenuItem>
              ) : (
                <SavedColumns savedColumnViews={savedColumnViews}
                  onItemSelect={onItemSelect}
                  onDelete={onDelete}
                  selectedViewName={selectedViewName} />
              )}
            </MenuList>
          </Paper>
        </Popper>
        {deleteDialog && (
          <DeletePrompt show
            entity={columnPreference}
            items={columnsForDelete}
            isLoading={isDeleting}
            overrideMessage={getDeleteMessage}
            onConfirm={onDeleteConfirm}
            onCancel={onDeleteCancel} />
        )}
        <IonSnackbar show={isShowSnackbar}
          zIndex={dlsZIndex('savedColumnSnackbar')}
          message={deleteSuccess}
          actionLabel={translate('common:editors.delete.ok')}
          onAction={onCloseMessage}
          onClose={onCloseMessage}
          autoHideDuration={HIDE_DURATION} />
      </div>
    </ClickAwayListener>
  );
};

export default SavedColumnList;
